import { type MaybeRef, computed, unref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import type { JsonApiResponse } from '~/interfaces/api/main';
import type { Opportunity } from '~/interfaces/opportunities/main';
import {
  type OpportunitiesListResult,
  type OpportunityDetailResult,
  getOpportunitiesList,
  getOpportunityByHashId,
} from '~/api/opportunities';

export const opportunitiesKeys = {
  root: () => ['opportunities'] as const,
  list: (params?: string | null) => {
    if (params) {
      return ['opportunities', 'list', params] as const;
    }
    return ['opportunities', 'list'] as const;
  },
  detail: (hashId?: string | null) => ['opportunities', 'detail', hashId ?? ''] as const,
};

type EnabledRef = MaybeRef<boolean | undefined>;

interface UseOpportunitiesListOptions {
  enabled?: EnabledRef;
}

interface UseOpportunityQueryOptions {
  enabled?: EnabledRef;
}

export const EMPTY_LIST_RESULT: OpportunitiesListResult = {
  items: [],
  totalItems: 0,
  raw: { data: [], meta: { totalItems: 0 } },
};

export const EMPTY_DETAIL_RESULT: OpportunityDetailResult = {
  item: null,
  raw: { data: null },
};

export function useOpportunitiesListQuery (
  params?: MaybeRef<string | null | undefined>,
  options: UseOpportunitiesListOptions = {},
): {
    query: any;
    opportunities: globalThis.ComputedRef<Opportunity[]>;
    total: globalThis.ComputedRef<number>;
    setData: (items: Opportunity[], totalItems?: number) => void;
  } {
  const queryClient = useQueryClient();

  const enabled = computed<boolean>(() => {
    if (options.enabled === undefined) {
      return true;
    }
    return Boolean(unref(options.enabled));
  });

  const queryKey = computed(() => opportunitiesKeys.list(unref(params) ?? null));

  const query = useQuery<OpportunitiesListResult>({
    queryKey,
    queryFn: () => getOpportunitiesList(unref(params) ?? null),
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
    initialData: () => queryClient.getQueryData<OpportunitiesListResult>(queryKey.value) ?? EMPTY_LIST_RESULT,
  });

  const opportunities = computed<Opportunity[]>(() => query?.data?.value?.items ?? []);
  const total = computed<number>(() => query?.data?.value?.totalItems ?? 0);

  const setData = (items: Opportunity[], totalItems?: number) => {
    const resolvedTotal = typeof totalItems === 'number' ? totalItems : total.value;
    const currentRaw = query?.data?.value?.raw ?? EMPTY_LIST_RESULT.raw;

    queryClient.setQueryData(queryKey.value, {
      items: [...items],
      totalItems: resolvedTotal ?? items.length,
      raw: {
        ...currentRaw,
        data: [...items],
        meta: {
          ...currentRaw.meta,
          totalItems: resolvedTotal ?? items.length,
        },
      },
    } satisfies OpportunitiesListResult);
  };

  return {
    query,
    opportunities,
    total,
    setData,
  };
}

export function useOpportunityQuery (
  hashId: MaybeRef<string | null | undefined>,
  options: UseOpportunityQueryOptions = {},
) {
  const queryClient = useQueryClient();

  const enabled = computed<boolean>(() => {
    const requested = options.enabled === undefined ? true : Boolean(unref(options.enabled));
    const resolvedId = unref(hashId);
    return requested && Boolean(resolvedId);
  });

  const queryKey = computed(() => opportunitiesKeys.detail(unref(hashId) ?? null));

  const query = useQuery<OpportunityDetailResult>({
    queryKey,
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
    queryFn: () => {
      const resolvedId = unref(hashId);
      if (!resolvedId) {
        return EMPTY_DETAIL_RESULT;
      }
      return getOpportunityByHashId(resolvedId);
    },
    initialData: () => queryClient.getQueryData<OpportunityDetailResult>(queryKey.value) ?? EMPTY_DETAIL_RESULT,
  });

  const opportunity = computed<Opportunity | null>(() => query?.data?.value?.item ?? null);

  const setOpportunity = (item: Opportunity | null, hashIdOverride?: string | null): void => {
    const targetKey = opportunitiesKeys.detail(hashIdOverride ?? unref(hashId) ?? null);
    const currentRaw = query?.data?.value?.raw ?? (EMPTY_DETAIL_RESULT.raw as JsonApiResponse<Opportunity | null>);

    queryClient.setQueryData(targetKey, {
      item,
      raw: {
        ...currentRaw,
        data: item,
      },
    } satisfies OpportunityDetailResult);
  };

  return {
    query,
    opportunity,
    setOpportunity,
  };
}
